<template>
  <div class="modal-card">
    <b-loading :isFullPage="false" :active="isLoading" canCancel />
    <header class="modal-card-head">
      <p class="modal-card-title is-size-5 has-text-centered">
        {{ General.TEXTS.ATTACH_DOCUMENTS_PROCESS }}
      </p>
      <div class="close-button cursor-pointer" @click="close">
        <b-icon icon="close" />
      </div>
    </header>
    <div class="modal-content-container">
      <section class="spacing-p-10">
        <div>
          <b-field
            v-for="(file, index) in getFilesToUpload"
            :key="index"
            grouped
          >
            <div class="is-align-self-flex-end index-file">
              {{ formatIndex(index) }}
            </div>
            <b-field class="input-field" expanded>
              <template v-if="!index" #label>
                <span class="attachment-item-label">
                  {{ General.ATTACHMENT_NAME }}
                </span>
              </template>
              <b-input
                v-model="filesToUpload[index].attachment_name"
                size="is-small"
                expanded
                :disabled="isDisabled"
                :class="{
                  'same-line-counter input-counter-small': showInputCounter(
                    index
                  )
                }"
                v-bind="editable ? { maxlength: 70 } : {}"
                :hasCounter="showInputCounter(index)"
              />
              <template #message>
                <span class="attachment-item-footer">
                  {{
                    ` ${General.SIZE} :  ${bytes2Size(file.file.size)} / ${
                      General.MAX_SIZE
                    }: ${bytes2Size(maxFileSize)}`
                  }}
                </span>
              </template>
            </b-field>
            <b-field v-if="!editable" class="input-field" expanded>
              <template v-if="!index" #label>
                <span class="attachment-item-label">
                  {{ General.DOCUMENT_TYPE }}
                </span>
              </template>
              <b-autocomplete
                v-if="!editable"
                v-model="filesToUpload[index].document_type_name"
                size="is-small"
                :data="filterTypes"
                :disabled="isDisabled"
                :placeholder="General.TEXTS.TYPE_TO_SEARCH_DOCUMENT_TYPE"
                autocomplete
                field="type"
                name="id"
                keepFirst
                @typing="getFilterTypes"
                @select="option => selectType(index, option)"
              >
                <template slot="empty">
                  <span v-if="!isLoading">
                    {{ General.MESSAGES.NO_ATTACHMENT_TYPE }}
                  </span>
                  <b-loading v-else :isFullPage="false" :active="isLoading" />
                </template>
              </b-autocomplete>
            </b-field>
            <div
              class="is-align-self-flex-end close-button"
              @click.stop="removeFile(index)"
            >
              <b-icon icon="delete" size="is-small" />
            </div>
          </b-field>
        </div>
        <ProgressBar
          v-if="!editable"
          :currentSize="getCurrentAttachmentSize"
          :maxSize="getMaxAttachmentSize"
          :barLabel="General.TEXTS.LIMIT_PROCESS_ATTACHMENT"
          class="spacing-px-20 spacing-my-5"
        />
        <div class="observation-container">
          {{ description_info }}
          <b-tooltip
            v-if="editable"
            multilined
            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
            size="is-large"
            position="is-left"
            appendToBody
          >
            <template v-slot:content>
              <span class="has-text-weight-semibold is-flex">{{
                General.TEXTS.TITLE_INFO_FILE_UPLOAD_EDITABLE
              }}</span>
              <p class="has-text-left">
                {{ typesAcceptSort }}
              </p>
            </template>
            <div class="is-flex">
              <b-icon
                class="spacing-ml-10"
                :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                size="is-small"
                icon="information"
              />
            </div>
          </b-tooltip>
        </div>
        <b-field class="spacing-px-20">
          <b-upload
            v-model="listAttachedFiles"
            multiple
            dragDrop
            expanded
            :accept="typesAccept"
            class="vertical-align-custom"
            @input="getListAttachedFiles()"
            @invalid="() => (hasInvalidFile = true)"
          >
            <section class="spacing-p-5">
              <div class="has-text-centered has-text-weight-semibold">
                <b-icon
                  :type="isContrast ? 'is-white-dark' : 'is-light'"
                  icon="upload"
                  size="is-large"
                />
                <p>{{ General.TEXTS.DRAG_AND_DROP }}</p>
                <p class="is-lowercase">{{ Home.OR }}</p>
                <a class="underline">{{
                  General.TEXTS.SELECT_FROM_COMPUTER
                }}</a>
              </div>
            </section>
          </b-upload>
        </b-field>
      </section>

      <footer class="modal-card-foot is-justify-content-flex-end">
        <b-button
          :type="isContrast ? 'is-white-dark' : 'is-primary'"
          :disabled="isDisabled"
          @click.stop="submitFiles"
        >
          {{ General.SAVE.toUpperCase() }}
        </b-button>

        <b-button
          :type="isContrast ? 'is-white-dark' : 'is-primary'"
          outlined
          @click.stop="close"
        >
          {{ General.CANCEL.toUpperCase() }}
        </b-button>
      </footer>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { bytes2Size } from "@/utils/bytes2Size";
import { App, General, Home } from "@/utils/constants";

import ListUploadFiles from "@/components/ListUploadFiles";
import ProgressBar from "@/components/ProgressBar";
import {
  PROCESS_FILES_MAX_SIZE,
  DOCUMENT_TYPE as DOC_TYPE
} from "@/utils/enumerate";
import { v4 as uuidv4 } from "uuid";
import { getFileMimeType } from "@/utils/fileUtility";

export default {
  name: "ModalAttachments",

  components: {
    ProgressBar
  },

  props: {
    dispatchId: {
      type: String,
      default: ""
    },
    jointId: {
      type: String,
      default: ""
    },
    editable: {
      type: Boolean,
      default: false
    },
    isPrimary: {
      type: Boolean
    },
    isArchived: {
      type: Boolean,
      default: false
    },
    listProcess: {
      type: Array,
      default: () => []
    },
    onClose: {
      type: Function,
      default: () => {}
    },
    currentSizeProcess: {
      type: Number,
      default: 0
    },
    currentProcessId: {
      type: String,
      default: ""
    },
    isRevertNoEffect: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      App,
      General,
      Home,
      bytes2Size,
      listAttachedFiles: [],
      filesToUpload: [],
      filterTypes: [],
      indeterminate: false,
      size: "is-small",
      showValue: false,
      format: "raw",
      precision: 2,
      keepTrailingZeroes: false,
      maxFileSize: 209715200, //200 MB
      allowedFileType: [],
      notAllowedFileType: [], // editable files
      isLoading: false,
      hasInvalidFile: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      fileTypes: "getFileTypesByEditor",
      getModifiedProcessFiles: "getModifiedProcessFiles",
      countDocumentType: "countDocumentType",
      getProcessFilesEditable: "getModifiedProcessFilesEditable",
      getModifiedDispatchFilesEditable: "getModifiedDispatchFilesEditable",
      getModifiedDispatchFiles: "getModifiedDispatchFiles",
      getListAttachment: "getListAttachment",
      getListUploadFiles: "getListUploadFiles",
      isContrast: "isContrast",
      extensionList: "getFileExtensionList"
    }),

    typeFile() {
      if (this.isArchived) return "archived";
      else if (this.dispatchId) return "dispatch";
      else if (this.isRevertNoEffect) return "restoreEffect";
      else if (this.jointId) return "join";
      else return "process";
    },

    typesAcceptSort() {
      const list_ext = this.typesAccept.split(", ");
      if (list_ext.length > 1) {
        return `${list_ext.slice(0, -1).join(", ")} e ${list_ext.at(-1)}`;
      }
      return list_ext.join(", ");
    },

    typesAccept() {
      return (this.editable
        ? this.extensionList.editable.map(
            extensionObject => `.${extensionObject.name}`
          )
        : this.extensionList.notEditable.map(
            extensionObject => `.${extensionObject.name}`
          )
      )
        .sort()
        .join(", ");
    },

    description_info() {
      if (this.editable) return General.TEXTS.ALERT_FILE_UPLOAD_EDITABLE;
      else return General.TEXTS.ALERT_FILE_UPLOAD;
    },

    getFilesToUpload() {
      return this.filesToUpload || [];
    },

    getNumberDocuments() {
      const countUpdate = this.countDocumentType(this.typeFile);
      return countUpdate + this.getFilesToUpload.length;
    },

    isDisabled() {
      let disabled = false;
      if (
        this.getCurrentAttachmentSize <= 0 ||
        (!this.editable &&
          this.getCurrentAttachmentSize >= this.getMaxAttachmentSize) ||
        this.isLoading
      ) {
        disabled = true;
      }
      return disabled;
    },

    getCurrentAttachmentSize() {
      return (
        this.filesToUpload?.reduce((acc, actual) => acc + actual.file.size, 0) +
        this.currentSizeProcess
      );
    },

    getMaxAttachmentSize() {
      return PROCESS_FILES_MAX_SIZE;
    }
  },

  watch: {
    hasInvalidFile: {
      handler() {
        if (this.hasInvalidFile) {
          this.invalidExtensionUpload();
          this.hasInvalidFile = false;
        }
      }
    }
  },

  async beforeMount() {
    this.isLoading = true;
    this.getFilterTypes("");
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      uploadTrigger: "uploadTrigger"
    }),

    ...mapMutations({
      setListUploadFiles: "setListUploadFiles",
      clearListUploadFiles: "clearListUploadFiles"
    }),

    close() {
      this.$parent.close();
      this.onClose();
    },

    openAlertMessage(message, duration = 8000) {
      this.snackToast.open({
        message: message,
        type: "is-danger",
        duration: duration
      });
    },

    removeFile(index) {
      this.filesToUpload.splice(index, 1);
    },

    formatIndex(index) {
      return `${(index + 1).toString().padStart(2, "0")}.`;
    },

    getFilterTypes(text) {
      this.filterTypes = this.fileTypes(1).filter(option =>
        option.type.toLowerCase().includes(text.toLowerCase())
      );
    },

    selectType(index, option) {
      if (option) {
        this.filesToUpload[index].document_type_name = option.type;
        this.filesToUpload[index].document_type = option.id;
      }
    },

    async uploadFiles(file) {
      const mineType = await getFileMimeType(file);
      if (!this.editable && mineType !== "application/pdf") {
        this.openAlertMessage(App.PartAddressScreen.MESSAGES.FILE_BROKEN);
      } else if (!this.isPrimary || this.filesToUpload.length < 1)
        this.filesToUpload.push({
          process_id: this.currentProcessId,
          attachment_name: file.name,
          document_type: DOC_TYPE.ATTACHMENT, // default
          file,
          capacity_id: this.user.capacity.capacity_id,
          dispatch_id: this.dispatchId || null,
          document_join_id: this.jointId || null,
          editable: this.editable,
          primary: this.isPrimary
        });
      else
        this.openAlertMessage(
          App.PartAddressScreen.MESSAGES.ONLY_ONE_SOLICITATION
        );
    },

    async getListAttachedFiles() {
      const attachedFilesSize = this.listAttachedFiles?.reduce(
        (acc, actual) => acc + actual.size,
        0
      );

      if (
        !this.editable &&
        this.getCurrentAttachmentSize + attachedFilesSize >
          this.getMaxAttachmentSize
      ) {
        this.openAlertMessage(General.TEXTS.LIMIT_PROCESS_SIZE);
        this.listAttachedFiles = [];
        return;
      }
      for (const file of this.listAttachedFiles) {
        const maxFileName = this.editable ? 70 : 100;
        if (file.name.length >= maxFileName) {
          this.openAlertMessage(
            `${General.TEXTS.LIMIT_ATTACH_NAME.replace("LIMIT", maxFileName)}.`
          );
        } else if (file.size > this.maxFileSize) {
          if (file.size / this.maxFileSize > 1) {
            this.openAlertMessage(
              `${
                this.General.MESSAGES.THE_FILE_SIZE_EXCEEDS_ALLOWED_LIMIT
              } <b>${bytes2Size(this.maxFileSize)}</b>.`
            );
          }

          this.openAlertMessage(
            App.PartAddressScreen.MESSAGES.FILE_SIZE_OVERFLOW.replace(
              "FILE_NAME",
              file.name
            ).replace("FILE_SIZE", bytes2Size(file.size))
          );
        } else await this.uploadFiles(file);
      }
      this.listAttachedFiles = [];
    },

    invalidExtensionUpload() {
      if (this.editable)
        this.openAlertMessage(General.TEXTS.WARNING_ATTACHMENT_EDITABLE);
      else this.openAlertMessage(General.TEXTS.WARNING_ATTACHMENT_PDF);
    },

    async submitFiles() {
      this.isLoading = true;
      let filesToProcess = this.filesToUpload;
      if (this.listProcess.length) {
        filesToProcess = [];
        this.listProcess.forEach(process =>
          this.filesToUpload.forEach(file =>
            filesToProcess.push({
              ...file,
              process_id: process.id,
              archive: !this.isRevertNoEffect,
              revert_no_effect: this.isRevertNoEffect
            })
          )
        );
      }

      filesToProcess = filesToProcess.map((file, sequence) => ({
        ...file,
        sequence,
        file_index: uuidv4(),
        percentage: 0,
        status: "pending"
      }));

      this.setListUploadFiles(filesToProcess);

      this.snackQueue.open({
        id: "upload_files",
        parent: this,
        title: this.General.TEXTS.UPLOAD_QUANTITY.replace(
          "(QUANTITY)",
          this.getListUploadFiles.length
        ),
        closeAction: this.clearListUploadFiles,
        component: ListUploadFiles
      });

      this.uploadTrigger();

      this.isLoading = false;
      this.close();
    },

    showInputCounter(index) {
      return (
        this.filesToUpload[index].attachment_name.length == 70 && this.editable
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  width: 60vw;
  max-width: 960px;
  max-height: 75vh;
  background-color: $color-1;

  @media only screen and (max-width: $tablet) {
    width: 100%;
  }
}

.modal-card-body {
  overflow: auto;
  max-height: 40vh;
}

.attachment-item {
  display: flex;
  flex: 1;
  position: relative;
  width: 100%;
  flex-flow: row;
  gap: 0.1rem;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  flex-grow: 1;
}

.attachment-item-label {
  padding-left: 3px;
  font-weight: 600;
}

.attachment-item-footer {
  color: $light;
  padding-left: 5px;
  font-weight: 600;
  font-size: 0.7rem;
}

.columns-titles {
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  font-size: 0.85rem;
}

.columns-titles div:nth-child(1) {
  font-weight: 500;
  padding-left: 2.8rem;
}

.columns-titles div:nth-child(2) {
  font-weight: 500;
  padding-left: 10rem;
}

.index-file {
  width: 1.8rem;
  margin-bottom: 28px;
}

.close-button {
  cursor: pointer;
  .icon {
    margin-bottom: 28px;
  }
  &:hover {
    opacity: 0.7;
  }
}

.spacing-mt-13 {
  margin-top: 13px;
}

.underline {
  text-decoration: underline;
}

.progress-wrapper {
  margin-bottom: 0;
}

.vertical-align-custom {
  display: flex;
  align-items: center;

  section {
    background-color: $color-2;
    color: $color-7;

    a:hover {
      color: $dark-white;
    }
  }
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
}

.observation-container {
  font-size: 1rem;
  color: $color-7;
  font-weight: 600;
  margin-bottom: 5px;
  padding: 0 20px;
}
</style>

<style lang="scss">
.upload .upload-draggable {
  padding: 0;
}
</style>
